import type { LeaderboardEntry } from './leaderboard.types';

type Row = Record<string, unknown>;

/** Rows of either route, best first, ranked 1..N on the contribution count. */
export function normalizeRows(result: unknown, limit = 100): LeaderboardEntry[] {
  const rows: Row[] = Array.isArray(result) ? (result as Row[]) : (((result as Row | null)?.users as Row[]) ?? []);
  return rows
    .slice()
    .sort((a, b) => (Number(b.contributions) || 0) - (Number(a.contributions) || 0))
    .slice(0, limit)
    .map((r, i) => {
      const username = typeof r.username === 'string' ? r.username : '';
      const displayName =
        (typeof r.displayName === 'string' && r.displayName.trim()) ||
        username ||
        (r.denonymizedId ? `Utilisateur ${r.denonymizedId}` : '') ||
        'Anonyme';
      const id = r.userId ?? r.id;
      return {
        rank: i + 1,
        contributions: Number(r.contributions) || 0,
        displayName,
        username,
        userId: id === undefined || id === null ? undefined : String(id),
        languages: Array.isArray(r.languages) ? (r.languages as string[]) : [],
      };
    });
}

/**
 * The name shown on the public board. The backend falls back to the email
 * when an account has no name nor username: only its local part is shown.
 */
export function publicName(entry: Pick<LeaderboardEntry, 'displayName' | 'username'>): string {
  const name = entry.displayName.trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) return entry.username || name.split('@')[0];
  return name || entry.username || 'Anonyme';
}

export interface CurrentUserRef {
  id?: string | number | null;
  username?: string | null;
}

export function isCurrentUser(entry: LeaderboardEntry, user: CurrentUserRef | null | undefined): boolean {
  if (!user) return false;
  if (entry.userId !== undefined && user.id !== undefined && user.id !== null && String(entry.userId) === String(user.id)) return true;
  return Boolean(user.username && entry.username && entry.username === user.username);
}

export function findCurrentEntry(entries: LeaderboardEntry[], user: CurrentUserRef | null | undefined) {
  return user ? entries.find((entry) => isCurrentUser(entry, user)) : undefined;
}

export interface NextRankGap {
  /** The contributor just above. */
  above: LeaderboardEntry;
  /** Contributions still needed to pass them (ties go to the one above). */
  needed: number;
}

/** How far the entry is from the rank above it; null at the top or when absent. */
export function gapToNextRank(entries: LeaderboardEntry[], entry: LeaderboardEntry | undefined): NextRankGap | null {
  if (!entry) return null;
  const index = entries.indexOf(entry);
  if (index <= 0) return null;
  const above = entries[index - 1];
  return { above, needed: Math.max(1, above.contributions - entry.contributions + 1) };
}

/** Top three for the podium, the rest for the table. */
export function splitPodium(entries: LeaderboardEntry[]) {
  return { podium: entries.slice(0, 3), rest: entries.slice(3) };
}

const fold = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Search on the shown name and the username, accents ignored. */
export function filterEntries(entries: LeaderboardEntry[], query: string): LeaderboardEntry[] {
  const q = fold(query.trim());
  if (!q) return entries;
  return entries.filter((entry) => fold(publicName(entry)).includes(q) || fold(entry.username).includes(q));
}
